import { createVector, createMatrix, clone, resizePackTable } from '../other/utilities';

export const createSimpleDefaultPackTable = (    
    rowCount = 1,
    columnCount = 1
) => ({
    tableData: {
        value : createMatrix(rowCount, columnCount, "")
    },
    GBFixedRows: 1,
    GBFixedCols: 1,
    GBRowCount: rowCount,
    GBColCount: columnCount,
    TotalRow: 0,
    TotalCol: 0,
    WordWrappedCols: createVector(columnCount, false),
    RDec: createMatrix(rowCount, columnCount, 0),
    MinAllowedVal: createMatrix(rowCount, columnCount, 0),
    MaxAllowedVal: createMatrix(rowCount, columnCount, 0),
    FontStyles: createMatrix(rowCount, columnCount, { intArray: [] }),
    FontColors: createMatrix(rowCount, columnCount, "#000"),
    BGColors: createMatrix(rowCount, columnCount, 536870911),
    GBCellComment: createMatrix(rowCount, columnCount, ""),
    GBUseTriangle: createMatrix(rowCount, columnCount, false),
    LockedCells: createMatrix(rowCount, columnCount, false),
    MergedCells: [],
    IndentRowText: createVector(rowCount, {
        xoffset: 0,
        Value: false
    }),
    RowSubHeadings: createVector(rowCount, false),
    HasCheckBox: createMatrix(rowCount, columnCount, false),
    CheckBoxState: createMatrix(rowCount, columnCount, false),
    Alignments: createMatrix(rowCount, columnCount, 0),
    Title: "",
    editorMstID: 0,
    Proj: 0,
    ModID: 0,
    RowIds: createMatrix(rowCount, 2, [0, 0]),
    ColIds: createMatrix(columnCount, 2, [0, 0]),
    SourceMode: 0,
    SourceMap: [],
    Source: [""],
    GBRowHeights : createVector(rowCount, -1),
    GBColWidths : createVector(columnCount, -1),
});

export const createDefaultPackTable = (
    data,
    fixedRowCount,
    fixedColumnCount,
    rowCount,
    columnCount,
    displayTotalRow,
    displayTotalColumn
) => ({
    tableData: data,
    GBFixedRows: fixedRowCount,
    GBFixedCols: fixedColumnCount,
    GBRowCount: rowCount,
    GBColCount: columnCount,
    TotalRow: displayTotalRow ? 1 : 0,
    TotalCol: displayTotalColumn ? 1 : 0,
    WordWrappedCols: createVector(columnCount, false),
    RDec: createMatrix(rowCount, columnCount, 0),
    MinAllowedVal: createMatrix(rowCount, columnCount, 0),
    MaxAllowedVal: createMatrix(rowCount, columnCount, 0),
    FontStyles: createMatrix(rowCount, columnCount, { intArray: [] }),
    FontColors: createMatrix(rowCount, columnCount, "#000"),
    BGColors: createMatrix(rowCount, columnCount, 536870911),
    GBCellComment: createMatrix(rowCount, columnCount, ""),
    GBUseTriangle: createMatrix(rowCount, columnCount, false),
    LockedCells: createMatrix(rowCount, columnCount, false),
    MergedCells: [],
    IndentRowText: createVector(rowCount, {
        xoffset: 0,
        Value: false
    }),
    RowSubHeadings: createVector(rowCount, false),
    HasCheckBox: createMatrix(rowCount, columnCount, false),
    CheckBoxState: createMatrix(rowCount, columnCount, false),
    Alignments: createMatrix(rowCount, columnCount, 0),
    Title: "",
    editorMstID: 0,
    Proj: 0,
    ModID: 0,
    RowIds: createMatrix(rowCount, 2, [0, 0]),
    ColIds: createMatrix(columnCount, 2, [0, 0]),
    SourceMode: 0,
    SourceMap: [],
    Source: [""],
    GBRowHeights : createVector(rowCount, -1),
    GBColWidths : createVector(columnCount, -1)
});

export const mergePackTables = (pt1, pt2) => {

    const defaultPackTable1 = createSimpleDefaultPackTable(
        pt1.GBRowCount || 1,
        pt1.GBColCount || 1,
    );

    const defaultPackTable2 = createSimpleDefaultPackTable(
        pt2.GBRowCount || 1,
        pt2.GBColCount || 1,
    );
    
    let packTable1  = {
        ...defaultPackTable1,
        ...pt1
    };
    
    let packTable2  = {
        ...defaultPackTable2,
        ...pt2
    };

    // Make sure columns are the same dimensions
    if (!(packTable1.GBColCount === packTable2.GBColCount)) {
        return {
            packTable: {},
            error: true
        };
    }

    let rowCount = packTable1.GBRowCount + packTable2.GBRowCount - packTable2.GBFixedRows;
    let columnCount = packTable1.GBColCount;
    let table = clone(packTable1);
    table = resizePackTable(table, rowCount, columnCount);

    let r2 = packTable1.GBRowCount;
    let c2;
    let value2 = packTable2.tableData.value;
    for (let r = packTable2.GBFixedRows; r < value2.length; r++) {
        c2 = 0;
        for (let c = 0; c < value2[r].length; c++) {
            table.tableData.value[r2][c2] = packTable2.tableData.value[r][c];
            table.WordWrappedCols[c2] = packTable2.WordWrappedCols[c];    
            table.RDec[r2][c2] = packTable2.RDec[r][c];
            table.MinAllowedVal[r2][c2] = packTable2.MinAllowedVal[r][c];
            table.MaxAllowedVal[r2][c2] = packTable2.MaxAllowedVal[r][c];
            table.FontStyles[r2][c2] = packTable2.FontStyles[r][c];
            table.FontColors[r2][c2] = packTable2.FontColors[r][c];
            table.BGColors[r2][c2] = packTable2.BGColors[r][c];
            table.GBCellComment[r2][c2] = packTable2.GBCellComment[r][c];
            table.GBUseTriangle[r2][c2] = packTable2.GBUseTriangle[r][c];
            table.LockedCells[r2][c2] = packTable2.LockedCells[r][c];    
            table.IndentRowText[r2] = packTable2.IndentRowText[r]; 
            table.RowSubHeadings[r2] = packTable2.RowSubHeadings[r]; 
            table.HasCheckBox[r2][c2] = packTable2.HasCheckBox[r][c]; 
            table.CheckBoxState[r2][c2] = packTable2.CheckBoxState[r][c]; 
            table.Alignments[r2][c2] = packTable2.Alignments[r][c]; 
            table.RowIds[r2] = packTable2.RowIds[r];
            table.ColIds[c2] = packTable2.ColIds[c];
            table.GBRowHeights[r2] = packTable2.GBRowHeights[r];
            table.GBColWidths[c2] = packTable2.GBColWidths[c];

            c2++;
        }
        r2++;
    }

    // Since we merged two unknown packTables, these settings should go to some default.
    table.MergedCells = [];    
    table.TotalRow = 0;
    table.TotalCol = 0;
    table.Title = "Merged";
    table.editorMstID = -1;
    table.Proj = -1;
    table.ModID = -1;
    table.SourceMode = 0;
    table.Source = [];

    return {
        packTable: table,
        error: false
    };
};
